import styled, { css, keyframes } from 'styled-components';

const aparecer = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const crescer = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`;

const tracar = keyframes`
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

/* Tokens do design system do protótipo (Copasul Design System), valendo só
   dentro desta página. As cores de marca são as mesmas do GlobalStyle
   (--first-color = brand-800, --second-color = brand-600...), só com os nomes
   do design system, para o CSS abaixo ficar igual ao do protótipo e poder ser
   conferido lado a lado. */
/* Justificado como no protótipo, menos no celular: em coluna estreita o
   justify abre buracos entre as palavras. */
const justificado = css`
  text-align: justify;

  @media (max-width: 600px) {
    text-align: left;
  }
`;

export const Pagina = styled.div`
  --brand-900: #0a3a1f;
  --brand-800: #0f4e2b;
  --brand-600: #00893a;
  --brand-500: #34a24f;
  --brand-400: #66b336;
  --brand-200: #b2d7af;
  --brand-100: #eaf6ee;
  --ink-900: #1b2420;
  --ink-700: #3f4a44;
  --ink-600: #616160;
  --ink-400: #838283;
  --ink-200: #c3ccc7;
  --ink-100: #eef2ef;
  --paper: #f8faf9;
  --white: #fff;

  --text-body: var(--ink-600);
  --text-strong: var(--ink-900);
  --text-secondary: var(--ink-700);
  --text-muted: var(--ink-400);
  --border-hairline: var(--ink-100);
  --border-strong: var(--ink-200);
  --accent: var(--brand-600);
  --accent-wash: var(--brand-100);

  --fs-display: clamp(2.25rem, 1rem + 4vw, 4rem);
  --fs-h2: clamp(1.25rem, 0.85rem + 1.4vw, 2rem);
  --fs-h3: clamp(1.125rem, 0.98rem + 0.5vw, 1.5rem);
  --fs-h4: clamp(1rem, 0.95rem + 0.3vw, 1.125rem);
  --fs-body: clamp(0.875rem, 0.82rem + 0.2vw, 1rem);
  --fs-small: clamp(0.75rem, 0.72rem + 0.1vw, 0.8125rem);
  --fs-eyebrow: 0.75rem;

  --sp-1: 0.25rem;
  --sp-2: 0.5rem;
  --sp-3: 0.75rem;
  --sp-4: 1rem;
  --sp-5: 1.5rem;
  --sp-6: 2rem;
  --sp-7: 3rem;
  --sp-8: 4rem;
  --section-y: clamp(3rem, 8vw, 6rem);
  --gutter: clamp(1rem, 4vw, 2.5rem);
  /* Uma largura para a página inteira, a dos Destaques (antes só eles iam a
     1400px e o resto ficava em 1200px, desalinhado). A Capa também lê esta. */
  --largura-pagina: 1400px;

  --r-sm: 4px;
  --r-md: 8px;
  --r-lg: 16px;
  --r-xl: 24px;
  --r-full: 999px;
  --sh-sm: 0 1px 2px rgba(27, 36, 32, 0.06);
  --sh-md: 0 4px 12px rgba(27, 36, 32, 0.08);
  --sh-lg: 0 12px 32px rgba(27, 36, 32, 0.12);
  --t-fast: 150ms ease-out;
  --t-base: 250ms ease-out;

  /* Altura do menu "Nesta página": a rolagem para uma seção para abaixo dele. */
  --nav-h: 3.5rem;

  background: var(--white);
  color: var(--text-body);
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  font-size: var(--fs-body);
  line-height: 1.6;
  text-align: left;

  a:focus-visible,
  button:focus-visible,
  select:focus-visible,
  [tabindex]:focus-visible {
    outline: 2px solid var(--brand-600);
    outline-offset: 2px;
    border-radius: var(--r-sm);
  }

  strong {
    font-weight: 700;
  }
`;

/* Revela o bloco ao entrar na tela, como no protótipo: só CSS, pela linha do
   tempo de rolagem. Navegador sem suporte mostra o bloco direto -- a animação é
   enfeite e nunca pode esconder conteúdo. */
export const revelar = css`
  @supports (animation-timeline: view()) {
    animation: ${aparecer} 400ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
    animation-timeline: view();
    animation-range: entry 10% cover 26%;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const Container = styled.div`
  width: 100%;
  max-width: var(--largura-pagina);
  margin-inline: auto;
  padding-inline: var(--gutter);
  box-sizing: border-box;
`;

export const Secao = styled.section`
  padding-block: var(--section-y);
  background: ${({ $wash }) => ($wash ? 'var(--accent-wash)' : 'transparent')};
  scroll-margin-top: var(--nav-h);
`;

/* ------------------------------------------------------------- tipografia */

export const Chapeu = styled.p`
  font-size: var(--fs-eyebrow);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: ${({ $cor }) => $cor || 'var(--brand-800)'};
`;

export const Rotulo = styled.p`
  font-size: var(--fs-eyebrow);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: var(--text-muted);
`;

export const H2 = styled.h2`
  font-size: var(--fs-h2);
  line-height: 1.15;
  letter-spacing: -0.01em;
  font-weight: 700;
  color: var(--text-strong);
  text-wrap: balance;
`;

export const H3 = styled.h3`
  font-size: var(--fs-h3);
  line-height: 1.2;
  font-weight: 700;
  color: var(--text-strong);
  text-wrap: balance;
`;

export const H4 = styled.h3`
  font-size: var(--fs-h4);
  line-height: 1.2;
  font-weight: 700;
  color: var(--text-strong);
`;

export const Paragrafo = styled.p`
  font-size: var(--fs-body);
  line-height: 1.6;
  color: var(--text-body);
  ${justificado}
  text-wrap: pretty;
`;

export const Nota = styled.p`
  font-size: var(--fs-small);
  line-height: 1.55;
  color: var(--text-muted);
  text-wrap: pretty;
`;

/* Texto em Markdown vindo do Squidex. */
export const Texto = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  font-size: var(--fs-body);
  line-height: 1.6;
  color: var(--text-body);
  ${justificado}

  p {
    text-wrap: pretty;
  }

  ul,
  ol {
    display: flex;
    flex-direction: column;
    gap: var(--sp-1);
    padding-left: var(--sp-5);
  }

  ul li {
    list-style: disc;
  }

  ol li {
    list-style: decimal;
  }

  a,
  a:visited {
    color: var(--brand-600);
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`;

export const Cabecalho = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  max-width: 68ch;
  margin-bottom: var(--sp-7);
`;

/* ----------------------------------------------------------------- botões */

const botoes = {
  primario: css`
    background: var(--brand-600);
    --cor-botao: var(--white);
    &:hover {
      background: var(--brand-500);
    }
  `,
  vazado: css`
    border: 1px solid rgba(255, 255, 255, 0.7);
    --cor-botao: var(--white);
    &:hover {
      background: rgba(255, 255, 255, 0.14);
    }
  `,
  contorno: css`
    border: 1px solid var(--border-strong);
    --cor-botao: var(--brand-800);
    &:hover {
      background: var(--accent-wash);
      border-color: var(--accent);
    }
  `,
  escuro: css`
    min-height: 56px;
    padding: var(--sp-3) var(--sp-6);
    background: var(--brand-800);
    --cor-botao: var(--white);
    font-size: var(--fs-body);
    &:hover {
      background: var(--brand-600);
    }
    &:active {
      background: var(--brand-900);
    }
  `,
};

export const Botao = styled.a`
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  align-self: flex-start;
  min-height: 44px;
  padding: 0 var(--sp-5);
  border-radius: var(--r-full);
  font-size: var(--fs-small);
  font-weight: 700;
  line-height: 1.3;
  text-decoration: none;
  transition: background var(--t-fast), border-color var(--t-fast);

  ${({ $variante }) => botoes[$variante] || botoes.contorno}

  /* A cor do texto vale em todos os estados. Sem isto, o a:visited do
     GlobalStyle (color: inherit) vencia depois do primeiro clique, e o texto
     do botão ficava cinza. O && dobra a classe para ganhar dele. */
  &&,
  &&:visited,
  &&:hover {
    color: var(--cor-botao);
  }

  /* Seta do link externo: sobe na diagonal no hover. */
  span[aria-hidden] {
    transition: transform var(--t-fast);
  }
  &:hover span[aria-hidden] {
    transform: translate(3px, -3px);
  }
`;

/* -------------------------------------------------------------------- ODS */

export const OdsLista = styled.span`
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
`;

/* O nome do ODS aparece num balão ao passar o mouse ou focar pelo teclado. */
export const Ods = styled.span`
  position: relative;
  display: block;
  line-height: 0;
  border-radius: var(--r-sm);

  img {
    display: block;
    border-radius: var(--r-sm);
  }

  &::after {
    content: attr(data-ods);
    position: absolute;
    left: 50%;
    bottom: calc(100% + 8px);
    transform: translateX(-50%);
    width: max-content;
    max-width: 210px;
    text-align: center;
    background: var(--ink-900);
    color: var(--white);
    font-size: var(--fs-eyebrow);
    line-height: 1.35;
    padding: 6px 10px;
    border-radius: var(--r-sm);
    box-shadow: var(--sh-md);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity var(--t-fast), visibility var(--t-fast);
    z-index: 5;
  }

  &:hover::after,
  &:focus-visible::after {
    opacity: 1;
    visibility: visible;
  }

  /* O último da fila abre o balão para a esquerda, senão sai da tela. */
  &:last-child::after {
    left: auto;
    right: 0;
    transform: none;
  }
`;

/* ------------------------------------------------------ menu "Nesta página" */

export const Nav = styled.nav`
  position: sticky;
  top: 0;
  z-index: 800;
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: saturate(140%) blur(14px);
  -webkit-backdrop-filter: saturate(140%) blur(14px);
  border-bottom: 1px solid var(--border-hairline);
  box-shadow: 0 1px 2px rgba(27, 36, 32, 0.06);
`;

export const NavTrilho = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--sp-5);
  min-height: var(--nav-h);
  overflow-x: auto;
  overscroll-behavior-x: contain;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  > * {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  > p {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 500;
    color: var(--text-muted);
  }

  a,
  a:visited {
    font-size: var(--fs-small);
    color: var(--text-body);
    text-decoration: none;
    padding-block: var(--sp-3);
    transition: color var(--t-fast);

    &:hover,
    &[aria-current='true'] {
      color: var(--accent);
    }
    &[aria-current='true'] {
      font-weight: 500;
    }
  }

  /* Sublinhado que desliza até a seção atual. A posição vem do JS, em
     --ind-x/--ind-w, medida no próprio link. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: var(--ind-w, 0px);
    height: 2px;
    background: var(--accent);
    border-radius: var(--r-full);
    transform: translateX(var(--ind-x, 0px));
    transition: transform var(--t-base), width var(--t-base);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }
  }
`;

/* --------------------------------------------------------- posicionamento */

export const Posicionamento = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  gap: var(--sp-8);
  align-items: stretch;

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    max-width: 68ch;

    ${Cabecalho} {
      margin-bottom: 0;
    }
  }
`;

export const Pilares = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
`;

export const Pilar = styled.a`
  position: relative;
  display: flex;
  flex: 1 1 0;
  min-height: 210px;
  border-radius: var(--r-lg);
  overflow: hidden;
  text-decoration: none;

  > img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 500ms ease;
  }
  &:hover > img {
    transform: scale(1.03);
  }

  /* Duas camadas: a cor do pilar e um escuro por cima, para o texto branco
     passar do contraste mínimo em qualquer foto. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    inset: 0;
  }
  &::before {
    background: linear-gradient(
      90deg,
      rgba(${({ $rgb }) => $rgb}, 0.62) 0%,
      rgba(${({ $rgb }) => $rgb}, 0.44) 55%,
      rgba(${({ $rgb }) => $rgb}, 0.18) 100%
    );
  }
  &::after {
    background: linear-gradient(90deg, rgba(20, 28, 24, 0.66) 0%, rgba(20, 28, 24, 0.54) 62%, rgba(20, 28, 24, 0) 100%);
  }

  > span {
    position: absolute;
    inset: var(--sp-3);
    z-index: 1;
    border: 1px solid rgba(255, 255, 255, 0.4);
    border-radius: var(--r-md);
    pointer-events: none;
  }

  > div {
    position: relative;
    z-index: 2;
    max-width: 64%;
    padding: var(--sp-6);
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: var(--sp-2);

    @media (max-width: 600px) {
      max-width: 100%;
    }
  }

  strong {
    font-size: var(--fs-h4);
    line-height: 1.1;
    font-weight: 700;
    color: var(--white);

    span {
      opacity: 0.7;
      margin-right: 0.6em;
    }
  }

  p {
    font-size: var(--fs-small);
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.92);
    text-wrap: pretty;
  }

  em {
    margin-top: var(--sp-2);
    font-style: normal;
    font-size: var(--fs-small);
    font-weight: 700;
    color: var(--white);
  }
`;

/* ----------------------------------------------------------- materialidade */

export const Grupos = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-7);
`;

export const Grupo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  scroll-margin-top: calc(var(--nav-h) + var(--sp-4));

  > div:first-child {
    display: flex;
    align-items: baseline;
    gap: var(--sp-3);

    h3 {
      font-size: var(--fs-h4);
      line-height: 1.2;
      font-weight: 700;
      color: ${({ $cor }) => $cor};
    }

    p {
      font-size: var(--fs-eyebrow);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-weight: 700;
      color: var(--text-secondary);
    }
  }
`;

export const Tema = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: minmax(190px, 240px) 1fr auto;
  gap: var(--sp-4);
  align-items: center;
  background: var(--white);
  border: 1px solid var(--border-hairline);
  border-radius: var(--r-lg);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    border-radius: var(--r-sm) 0 0 var(--r-sm);
    background: ${({ $cor }) => $cor};
  }

  > p:first-child {
    padding: var(--sp-5);
    font-size: var(--fs-body);
    line-height: 1.3;
    font-weight: 700;
    color: var(--brand-900);
  }

  > p:nth-child(2) {
    padding-block: var(--sp-4);
    font-size: var(--fs-body);
    line-height: 1.6;
    color: var(--text-body);
    ${justificado}
    max-width: 68ch;
  }

  > span {
    justify-content: flex-end;
    flex-wrap: nowrap;
    padding-right: var(--sp-5);
  }

  /* O protótipo só tinha a versão larga: aqui a linha vira pilha. */
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 0;

    > p:first-child {
      padding-bottom: var(--sp-2);
    }
    > p:nth-child(2) {
      padding: 0 var(--sp-5) var(--sp-4);
    }
    > span {
      justify-content: flex-start;
      padding: 0 var(--sp-5) var(--sp-5);
    }
  }
`;

/* -------------------------------------------------------------- destaques */

export const Destaque = styled.article`
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-7);
  align-items: ${({ $alinhar }) => $alinhar || 'flex-start'};
  padding-top: var(--sp-7);
  border-top: 1px solid var(--border-hairline);

  & + & {
    margin-top: var(--sp-7);
  }

  > * {
    flex: 1 1 min(100%, 380px);
    min-width: 0;
  }
`;

export const DestaqueTexto = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
`;

export const TituloComOds = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-4);
  flex-wrap: wrap;

  ${H3} {
    flex: 1 1 10ch;
    min-width: 0;
  }

  ${OdsLista} {
    flex: 0 0 auto;
    flex-wrap: nowrap;
  }
`;

/* A caixa "desenhada à mão" do resultado. */
export const Resultado = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin: var(--sp-7) var(--sp-2) var(--sp-2);
  padding: var(--sp-4) var(--sp-5);
  background: var(--accent-wash);
  border: 2px solid var(--brand-500);
  border-radius: 255px 15px 225px 15px / 15px 225px 15px 255px;
  box-shadow: 4px 8px 24px rgba(0, 122, 94, 0.15);
  transform: rotate(-1.5deg);

  p:first-child {
    font-size: var(--fs-h4);
    line-height: 1.2;
    font-weight: 700;
    color: var(--brand-800);
  }

  p + p {
    font-size: var(--fs-small);
    line-height: 1.6;
    ${justificado}
    text-wrap: pretty;
  }
`;

export const Volumes = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  margin-top: var(--sp-2);
  padding: var(--sp-5);
  border: 1px solid var(--border-hairline);
  border-radius: var(--r-lg);

  > div {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }
`;

export const Volume = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);

  > div:first-child {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    font-size: var(--fs-small);

    strong {
      color: var(--text-strong);
    }
    span {
      color: var(--text-muted);
      font-variant-numeric: tabular-nums;
    }
  }

  > div:last-child {
    height: 8px;
    border-radius: var(--r-full);
    background: var(--accent-wash);
    overflow: hidden;

    span {
      display: block;
      height: 100%;
      border-radius: var(--r-full);
      background: var(--accent);
    }
  }
`;

const fotoEmpilhada = css`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border: 3px solid var(--white);
  border-radius: var(--r-lg);
  box-shadow: var(--sh-md);
  display: block;
  transition: transform var(--t-base);

  &:hover {
    transform: scale(1.03);
  }
`;

/* Desenho 1 -- colagem de até quatro fotos sobrepostas (Uso do solo). */
export const Colagem = styled.div`
  align-self: center;
  max-width: 100%;
  overflow: hidden;
  padding: var(--sp-8) var(--sp-2) var(--sp-2);
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  grid-auto-rows: clamp(28px, 5.6vw, 56px);

  img {
    ${fotoEmpilhada}
  }
  img:nth-child(1) {
    grid-column: 1 / 5;
    grid-row: 1 / 5;
    z-index: 1;
    margin-top: calc(var(--sp-7) * -1);
  }
  img:nth-child(2) {
    grid-column: 4 / 7;
    grid-row: 3 / 8;
    z-index: 2;
    margin-left: calc(var(--sp-5) * -1);
    margin-top: calc(var(--sp-4) * -1);
  }
  img:nth-child(3) {
    grid-column: 1 / 4;
    grid-row: 6 / 11;
    z-index: 3;
    margin-right: calc(var(--sp-5) * -1);
    margin-top: calc(var(--sp-4) * -1);
  }
  img:nth-child(4) {
    grid-column: 3 / 7;
    grid-row: 9 / 14;
    z-index: 4;
    margin-left: calc(var(--sp-4) * -1);
    margin-top: calc(var(--sp-4) * -1);
  }
  img:nth-child(n + 5) {
    display: none;
  }
`;

/* Desenho 2 -- faixa inclinada de produtos (Rastreabilidade). */
export const Rastreio = styled.div`
  align-self: flex-start;
  margin-top: var(--sp-7);
  display: flex;
  flex-direction: column;
  gap: var(--sp-7);

  @media (max-width: 800px) {
    margin-top: 0;
  }
`;

export const Faixa = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $n }) => $n}, minmax(0, 1fr));
  gap: 10px;
  margin: 0 16px;
  border-radius: var(--r-lg);
  overflow: hidden;
  transform: skewX(-6deg);

  > div {
    overflow: hidden;
  }

  img {
    display: block;
    width: calc(100% + 40px);
    max-width: none;
    margin-left: -20px;
    height: clamp(140px, 16vw, 200px);
    object-fit: cover;
    transform: skewX(6deg);
    transition: transform var(--t-base);

    &:hover {
      transform: skewX(6deg) scale(1.03);
    }
  }
`;

export const Folha = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-4);

  svg {
    flex-shrink: 0;
  }

  > span {
    width: 1px;
    align-self: stretch;
    background: var(--border-strong);
  }

  p:first-child {
    font-size: var(--fs-body);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    color: var(--brand-800);
  }

  p + p {
    font-size: var(--fs-body);
    line-height: 1.5;
    color: var(--text-muted);
  }
`;

export const Indicadores = styled.div`
  display: flex;
  gap: var(--sp-5) 0;
  padding-top: var(--sp-6);
  border-top: 1px solid var(--border-hairline);

  > div {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sp-2);
    padding: 0 var(--sp-3);

    & + div {
      border-left: 1px solid var(--border-hairline);
    }

    /* O último rótulo costuma ser uma frase: ganha o dobro da largura. */
    &:last-child:not(:first-child) {
      flex-grow: 2;
    }
  }

  > div > span {
    width: 48px;
    height: 48px;
    border-radius: var(--r-full);
    background: var(--accent-wash);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }

  strong {
    font-size: clamp(0.95rem, 2.1vw, 1.4rem);
    line-height: 1.1;
    white-space: nowrap;
    color: var(--brand-600);
  }

  p {
    font-size: 11px;
    line-height: 1.4;
    text-align: center;
    color: var(--text-muted);
  }

  @media (max-width: 480px) {
    flex-wrap: wrap;

    > div {
      flex: 1 1 40%;
    }
    > div:nth-child(odd) {
      border-left: 0;
    }
  }
`;

export const NumeroGrande = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  flex-wrap: wrap;
  padding: var(--sp-6);
  background: var(--accent-wash);
  border-radius: var(--r-lg);

  strong {
    font-size: clamp(2.5rem, 5.4vw, 3.25rem);
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--brand-800);
  }

  > span {
    width: 1px;
    align-self: stretch;
    min-height: 64px;
    background: var(--brand-400);
  }

  p {
    flex: 1 1 240px;
    min-width: 0;
    font-size: var(--fs-small);
    line-height: 1.65;
    text-wrap: pretty;
  }
`;

/* Desenho 3 -- o ciclo: fotos redondas ligadas por uma linha (Fio Eco). */
export const Ciclo = styled.div`
  max-width: 100%;
  overflow: hidden;
  padding-block: var(--sp-3);

  > div {
    position: relative;
    width: 100%;
    max-width: 460px;
    margin: 0 auto;
    aspect-ratio: 1;
  }

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: block;
  }

  img.foto {
    position: absolute;
    aspect-ratio: 1;
    transform: translate(-50%, -50%);
    border-radius: var(--r-full);
    object-fit: cover;
    box-shadow: 0 14px 36px rgba(27, 36, 32, 0.09);
    display: block;
    transition: transform var(--t-base);

    &:hover {
      transform: translate(-50%, -50%) scale(1.04);
    }
  }
  img.foto:nth-of-type(1) {
    left: 30%;
    top: 26%;
    width: 42%;
  }
  img.foto:nth-of-type(2) {
    left: 76%;
    top: 56%;
    width: 40%;
  }
  img.foto:nth-of-type(3) {
    left: 26%;
    top: 78%;
    width: 40%;
  }
  img.foto:nth-of-type(n + 4) {
    display: none;
  }

  > div > span {
    position: absolute;
    right: 2%;
    bottom: 4%;
    z-index: 2;
    max-width: 78%;
    height: 56px;
    padding: 0 var(--sp-4);
    border-radius: var(--r-full);
    background: var(--white);
    box-shadow: var(--sh-md);
    display: flex;
    align-items: center;

    img {
      width: 150px;
      max-width: 100%;
      height: auto;
      display: block;
    }
  }
`;

/* ---------------------------------------------------------- certificações */

export const Trilha = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);

  &::before {
    content: '';
    position: absolute;
    left: 11px;
    top: 12px;
    bottom: 12px;
    width: 2px;
    background: linear-gradient(180deg, var(--brand-400), rgba(52, 162, 79, 0.18));
  }
`;

export const Certificacao = styled.article`
  position: relative;
  padding-left: var(--sp-8);
  ${revelar}

  &::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 26px;
    width: 16px;
    height: 16px;
    border-radius: var(--r-full);
    background: var(--white);
    border: 2px solid var(--brand-400);
    box-sizing: border-box;
    transition: background 300ms ease-out, border-color 300ms ease-out;
  }
  &:focus-within::before {
    background: var(--brand-600);
    border-color: var(--brand-600);
  }
  /* A bolinha acende junto com o cartão -- antes era o hover do article, que
     inclui a margem à esquerda, e ela acendia sem o cartão reagir. */
  @media (hover: hover) {
    &:has(> div:hover)::before {
      background: var(--brand-600);
      border-color: var(--brand-600);
    }
  }

  @media (max-width: 860px) {
    padding-left: var(--sp-7);
  }
`;

export const CertCartao = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $inv }) =>
    $inv ? 'minmax(0, 1fr) minmax(0, 1.55fr)' : 'minmax(0, 1.55fr) minmax(0, 1fr)'};
  gap: var(--sp-7);
  align-items: center;
  padding: var(--sp-7);
  background: var(--white);
  border: 1px solid var(--border-hairline);
  border-radius: var(--r-xl);
  box-shadow: var(--sh-sm);
  overflow: hidden;
  transition: border-color 300ms ease-out;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background: var(--brand-600);
    transform: scaleY(0);
    transform-origin: top;
    transition: transform 300ms ease-out;
  }
  /* Só com mouse: no toque o :hover fica preso depois do toque. */
  @media (hover: hover) {
    &:hover {
      border-color: var(--brand-400);
    }
    &:hover::before {
      transform: scaleY(1);
    }
  }

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }

  ${({ $inv }) =>
    $inv &&
    css`
      > div:last-child {
        order: -1;
      }
    `}

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sp-5);
    padding: var(--sp-5);

    > div:last-child {
      order: 0;
    }
  }
`;

export const CertVisual = styled.div`
  display: grid;
  place-items: center;
  align-content: center;
  align-self: stretch;
  /* Mesma altura em todos os cartões, para os quadros não mudarem de tamanho
     de um para o outro conforme o texto ao lado. */
  min-height: 340px;
  gap: var(--sp-4);
  padding: var(--sp-5);
  border-radius: var(--r-lg);
  background: radial-gradient(120% 100% at 50% 0%, rgba(52, 162, 79, 0.1), rgba(52, 162, 79, 0) 70%), var(--paper);

  /* Logo e selo costumam vir em PNG com fundo branco: o multiply some com o
     branco sobre o fundo esverdeado, sem depender do arquivo enviado. */
  img.logo,
  figure img {
    mix-blend-mode: multiply;
  }

  img.logo {
    max-width: 100%;
    max-height: 320px;
    height: auto;
    opacity: 0.88;
    transition: transform 300ms ease-out, opacity 300ms ease-out;
  }
  /* O logo reage ao quadro dele, não ao cartão inteiro: com o cartão, ele
     ficava ampliado com o mouse já sobre o texto e não reagia ao entrar nele. */
  @media (hover: hover) {
    &:hover img.logo {
      transform: translateY(-5px) scale(1.03);
      opacity: 1;
    }
  }

  @media (max-width: 860px) {
    min-height: 260px;
  }
`;

export const Marcos = styled.div`
  display: flex;
  gap: var(--sp-5);
  flex-wrap: wrap;
  justify-content: center;

  figure {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
  }

  img {
    height: 74px;
    width: auto;
    display: block;
    transition: transform 300ms ease-out;
  }
  @media (hover: hover) {
    figure:hover img {
      transform: translateY(-4px);
    }
  }

  figcaption {
    font-size: var(--fs-eyebrow);
    font-weight: 700;
    color: var(--text-muted);
  }
`;

export const Contribui = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  flex-wrap: wrap;
  margin-top: var(--sp-2);
`;

/* ---------------------------------------------------------------- emissões */

export const Bloco = styled.div`
  ${revelar}
`;

export const GeeTopo = styled(Bloco)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(220px, 34%);
  gap: var(--sp-7);
  align-items: start;
  margin-bottom: var(--sp-8);

  ${Cabecalho} {
    margin-bottom: 0;
  }

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sp-6);
  }
`;

export const GeeIndicador = styled.aside`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;

  &::before {
    content: '';
    width: 40px;
    height: 3px;
    border-radius: var(--r-full);
    background: var(--accent);
    margin-bottom: var(--sp-4);
  }

  strong {
    font-size: clamp(2.25rem, 5vw, 3.25rem);
    line-height: 1.05;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--brand-900);
  }

  > span {
    font-size: var(--fs-h4);
    font-weight: 700;
    color: var(--accent);
  }

  hr {
    width: 64px;
    height: 1px;
    border: 0;
    background: var(--border-strong);
    margin: var(--sp-3) 0;
  }
`;

export const TituloPequeno = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const GeeDados = styled(Bloco)`
  display: flex;
  flex-direction: column;
  gap: var(--sp-8);

  > div {
    display: flex;
    flex-direction: column;
    gap: var(--sp-4);
  }
`;

export const GeeCartao = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
  padding: var(--sp-5);
  background: var(--white);
  border: 1px solid var(--border-hairline);
  border-radius: var(--r-lg);

  h4 {
    font-size: var(--fs-body);
    font-weight: 700;
    color: var(--text-strong);
  }
`;

export const GeeGrade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: var(--sp-5);
`;

export const Tabela = styled.div`
  padding: 0;
  overflow-x: auto;
  background: var(--white);
  border: 1px solid var(--border-hairline);
  border-radius: var(--r-lg);

  table {
    width: 100%;
    min-width: 480px;
    border-collapse: collapse;
    font-size: var(--fs-small);
  }

  th,
  td {
    padding: var(--sp-4);
  }

  thead th {
    background: var(--brand-600);
    color: var(--white);
    font-weight: 700;
    text-align: right;
  }
  thead th:first-child {
    text-align: left;
  }

  tbody th {
    text-align: left;
    font-weight: 400;
    color: var(--text-strong);
  }
  tbody td {
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: var(--text-body);
  }
  tbody tr + tr > * {
    border-top: 1px solid var(--border-hairline);
  }
  tbody tr:hover > * {
    background: var(--paper);
  }

  tbody tr.total > * {
    background: var(--accent-wash);
    font-weight: 700;
    color: var(--brand-900);
  }
`;

export const Barra = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);

  > div:first-child {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-3);
    font-size: var(--fs-small);

    strong {
      color: var(--text-strong);
    }
    span {
      color: var(--text-muted);
      font-variant-numeric: tabular-nums;
    }
  }

  > div:last-child {
    height: 12px;
    border-radius: var(--r-full);
    background: var(--ink-100);
    overflow: hidden;
  }

  > div:last-child > span {
    display: block;
    height: 100%;
    border-radius: var(--r-full);
    transform-origin: left center;

    @supports (animation-timeline: view()) {
      animation: ${crescer} 800ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      animation-timeline: view();
      animation-range: entry 15% cover 32%;
    }
    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  }
`;

export const Rosca = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-5);
  flex-wrap: wrap;

  > div:first-child {
    position: relative;
    width: 152px;
    height: 152px;
    border-radius: var(--r-full);
    flex-shrink: 0;
    /* A linha do tempo do giro é a própria rosca entrando na tela. */
    view-timeline-name: --rosca;

    > svg {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: block;
    }

    /* O anel "gira" até se completar conforme a rolagem. A faixa começa assim
       que a rosca aparece embaixo da tela e termina logo depois (entry 5% até
       cover 22%): o giro acontece com ela ainda na parte de baixo, e não quando
       já passou do meio -- no protótipo era cover 30%, e dava para rolar até
       ela sem ver o efeito. Sem suporte, o anel já nasce completo. */
    .varredura {
      stroke-dasharray: 1;
      stroke-dashoffset: 0;
    }

    @supports (animation-timeline: view()) {
      .varredura {
        animation: ${tracar} linear both;
        animation-timeline: --rosca;
        animation-range: entry 5% cover 22%;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .varredura {
        animation: none;
      }
    }

    > div {
      position: absolute;
      inset: 27px;
      border-radius: var(--r-full);
      background: var(--white);
      display: grid;
      place-items: center;
      text-align: center;
      font-size: var(--fs-eyebrow);
      line-height: 1.35;
      color: var(--text-muted);

      strong {
        font-size: var(--fs-small);
        font-variant-numeric: tabular-nums;
        color: var(--text-strong);
      }
    }
  }

  > ul {
    flex: 1 1 160px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: var(--sp-1);
  }

  li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-4);
    padding: 6px var(--sp-2);
    border-radius: var(--r-sm);
    font-size: var(--fs-small);
    transition: background var(--t-fast);

    &:hover {
      background: var(--accent-wash);
    }

    > span {
      display: inline-flex;
      align-items: center;
      gap: var(--sp-3);
      min-width: 0;
    }

    i {
      width: 12px;
      height: 12px;
      border-radius: var(--r-sm);
      flex-shrink: 0;
    }

    strong {
      font-variant-numeric: tabular-nums;
      color: var(--text-strong);
    }
  }
`;

export const Grafico = styled.svg`
  width: 100%;
  height: auto;
  display: block;
  overflow: visible;

  .ponto {
    cursor: default;
    outline: none;
  }
  .ponto .dica,
  .ponto .halo {
    opacity: 0;
    transition: opacity var(--t-fast);
  }
  .ponto:hover .dica,
  .ponto:focus-visible .dica,
  .ponto:hover .halo,
  .ponto:focus-visible .halo {
    opacity: 1;
  }

  @supports (animation-timeline: view()) {
    .linha {
      stroke-dasharray: 1;
      animation: ${tracar} 900ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      animation-timeline: view();
      animation-range: entry 18% cover 40%;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .linha {
      animation: none;
    }
  }
`;

export const Evento = styled(Bloco)`
  display: flex;
  flex-direction: column;
  gap: var(--sp-7);
  margin-top: var(--sp-8);
`;

export const Fotos = styled.div`
  display: grid;
  grid-template-columns: ${({ $n }) => ($n > 1 ? 'minmax(0, 1fr) minmax(0, 1.32fr)' : 'minmax(0, 1fr)')};
  gap: var(--sp-3);
  align-items: stretch;

  > figure:first-child {
    aspect-ratio: ${({ $n }) => ($n > 1 ? '512 / 646' : '16 / 9')};
  }

  > div {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);

    figure {
      flex: 1 1 0;
      min-height: 0;
    }
  }

  figure {
    position: relative;
    overflow: hidden;
    border-radius: 16px;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 450ms ease;
  }
  figure:hover img {
    transform: scale(1.025);
  }

  figcaption {
    position: absolute;
    inset: auto 0 0 0;
    padding: var(--sp-4);
    font-size: var(--fs-eyebrow);
    line-height: 1.45;
    color: var(--white);
    background: linear-gradient(0deg, rgba(27, 36, 32, 0.78), rgba(27, 36, 32, 0));
    opacity: 0;
    transition: opacity var(--t-base);
  }
  figure:hover figcaption,
  figure:focus-within figcaption {
    opacity: 1;
  }

  /* Sem hover no toque: a legenda fica sempre à vista. */
  @media (hover: none) {
    figcaption {
      opacity: 1;
    }
  }

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);

    > figure:first-child {
      aspect-ratio: 4 / 3;
    }
    > div {
      flex-direction: row;

      figure {
        height: 160px;
      }
    }
  }
`;

export const Marco = styled(Bloco)`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: var(--sp-6);
  margin-top: var(--sp-8);
  padding: var(--sp-6);
  background: var(--accent-wash);
  border-left: 3px solid #c98a18;
  border-radius: 0 var(--r-lg) var(--r-lg) 0;

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    min-width: 0;

    > p:first-child {
      font-size: var(--fs-eyebrow);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-weight: 700;
      color: #8a6818;
    }
  }

  h3 {
    display: flex;
    align-items: center;
    gap: var(--sp-2);
    font-size: var(--fs-h3);
    line-height: 1.2;
    font-weight: 700;
    color: #8f5f0f;
  }

  > div:last-child {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: var(--sp-2);
    text-align: right;

    strong {
      font-size: clamp(2rem, 4.4vw, 2.75rem);
      line-height: 1;
      letter-spacing: -0.02em;
      color: #b77912;
    }

    hr {
      width: 100%;
      min-width: 96px;
      height: 1px;
      border: 0;
      background: #d89b24;
    }

    span {
      font-size: var(--fs-eyebrow);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-weight: 700;
      color: #8a6818;
    }
  }

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sp-4);

    > div:last-child {
      align-items: flex-start;
      text-align: left;
    }
  }
`;

export const Registro = styled(Bloco)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-5);
  flex-wrap: wrap;
  margin-top: var(--sp-7);
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-hairline);

  > div {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    max-width: 48ch;
  }

  ${Botao} {
    align-self: center;
  }
`;

/* --------------------------------------------------------------- eucalipto */

export const EucTopo = styled(Bloco)`
  display: grid;
  grid-template-columns: ${({ $comMidia }) => ($comMidia ? 'minmax(0, 45fr) minmax(0, 55fr)' : 'minmax(0, 1fr)')};
  gap: var(--sp-7);
  align-items: center;

  ${Cabecalho} {
    margin-bottom: 0;
    max-width: 52ch;
  }

  @media (max-width: 860px) {
    grid-template-columns: minmax(0, 1fr);
    gap: var(--sp-5);
  }
`;

export const Midia = styled.figure`
  position: relative;
  margin: 0;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: var(--sh-md);
  aspect-ratio: 16 / 9;
  background: var(--brand-900);

  > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .react-player {
    position: absolute;
    inset: 0;
  }

  /* Só capa, ainda sem link do vídeo: a mesma sombra da capa do player, para
     a legenda branca continuar legível. */
  > .sombra {
    position: absolute;
    inset: 0;
    background: linear-gradient(0deg, rgba(10, 58, 31, 0.58) 0%, rgba(10, 58, 31, 0.08) 62%);
  }

  figcaption {
    position: absolute;
    left: var(--sp-5);
    right: var(--sp-5);
    bottom: var(--sp-5);
    z-index: 1;
    pointer-events: none;
    font-size: var(--fs-body);
    font-weight: 700;
    color: var(--white);
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.35);
  }
`;

export const SoParaLeitor = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;

export const Play = styled.span`
  position: relative;
  z-index: 1;
  width: 76px;
  height: 76px;
  border-radius: var(--r-full);
  background: var(--white);
  display: grid;
  place-items: center;
  box-shadow: var(--sh-lg);
  color: var(--brand-600);
  transition: transform var(--t-fast);
`;

/* A capa por cima do player: antes do primeiro play e sempre que o visitante
   clica fora do vídeo. É um botão de verdade (teclado e leitor de tela). Sem
   imagem, fica o verde escuro do fundo. */
export const CapaDoVideo = styled.button`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  cursor: pointer;
  background: ${({ $imagem }) => ($imagem ? `url("${$imagem}") center / cover no-repeat` : 'var(--brand-900)')};

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(0deg, rgba(10, 58, 31, 0.58) 0%, rgba(10, 58, 31, 0.08) 62%);
  }

  &:hover ${Play}, &:focus-visible ${Play} {
    transform: scale(1.06);
  }

  &:focus-visible {
    outline: 3px solid var(--white);
    outline-offset: -6px;
  }
`;

export const EucFaixa = styled(Bloco)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 210px), 1fr));
  gap: var(--sp-5);
  margin-top: var(--sp-8);

  > div {
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    padding: var(--sp-2) 0 var(--sp-2) var(--sp-5);
    border-left: 1px solid var(--border-strong);
    transition: transform var(--t-fast);

    &:hover {
      transform: translateY(-2px);
    }
  }

  strong {
    font-size: var(--fs-body);
    color: var(--text-strong);
  }
`;

export const Painel = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
  margin-top: var(--sp-5);
  padding: var(--sp-6);
  background: var(--white);
  border: 1px solid var(--border-hairline);
  border-radius: 20px;
  box-shadow: var(--sh-sm);

  @media (max-width: 600px) {
    padding: var(--sp-4);
  }
`;

export const PainelGrade = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: var(--sp-4);
  align-items: stretch;
`;

export const Quadro = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: var(--sp-5);
  border: 1px solid var(--border-hairline);
  border-radius: var(--r-lg);

  > p:first-child {
    font-size: var(--fs-small);
    text-align: center;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--brand-900);

    span {
      text-transform: none;
    }
  }
`;

export const Colunas = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: var(--sp-6);
  height: 240px;
  border-bottom: 1px solid var(--ink-100);

  > div {
    flex: 1;
    max-width: 150px;
    height: 100%;
    display: grid;
    grid-template-rows: auto 1fr auto;
    justify-items: center;
    gap: var(--sp-2);
  }

  > div > p {
    font-size: var(--fs-eyebrow);
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: var(--brand-900);
    text-align: center;
  }

  > div > div {
    width: 100%;
    min-height: 0;
    display: flex;
    align-items: flex-end;

    span {
      display: block;
      width: 100%;
    }
  }

  > div > strong {
    font-size: var(--fs-small);
    color: var(--text-body);
  }
`;

export const Linhas = styled.div`
  display: flex;
  flex-direction: column;

  > div {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--sp-3);
    align-items: baseline;
    padding: var(--sp-3) 0;
    border-bottom: 1px solid var(--ink-100);
    font-size: var(--fs-small);
  }

  > div:first-child {
    padding-top: 0;
    padding-bottom: var(--sp-2);
    font-size: var(--fs-eyebrow);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 700;
    color: var(--text-muted);

    span span {
      text-transform: none;
    }
  }

  strong {
    font-variant-numeric: tabular-nums;
    color: var(--text-strong);

    small {
      font-size: var(--fs-eyebrow);
      font-weight: 400;
      color: var(--text-muted);
    }
  }
`;

export const Leitura = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 230px), 1fr));
  gap: var(--sp-5);

  article {
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
    padding-top: var(--sp-4);
    border-top: 2px solid var(--brand-400);
  }

  article > span {
    font-size: var(--fs-eyebrow);
    letter-spacing: 0.12em;
    font-weight: 700;
    color: var(--brand-400);
  }

  article > small {
    font-size: var(--fs-eyebrow);
    letter-spacing: 0.08em;
    font-weight: 700;
    color: var(--text-muted);
  }

  article > strong {
    font-size: clamp(1.75rem, 3.6vw, 2.25rem);
    line-height: 1;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
    color: var(--brand-800);
  }
`;

export const Etapas = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 190px), 1fr));
  gap: var(--sp-5);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 11px;
    height: 1px;
    background: var(--brand-400);
    opacity: 0.5;

    @media (max-width: 820px) {
      display: none;
    }
  }

  > div {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    padding-top: var(--sp-6);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 5px;
      width: 13px;
      height: 13px;
      border-radius: var(--r-full);
      background: var(--white);
      border: 2px solid var(--brand-600);
    }

    > span {
      font-size: var(--fs-eyebrow);
      letter-spacing: 0.12em;
      font-weight: 700;
      color: var(--brand-400);
    }
    > strong {
      font-size: var(--fs-body);
      color: var(--text-strong);
    }
  }
`;

export const Metodologia = styled(Bloco)`
  display: flex;
  flex-direction: column;
  gap: var(--sp-2);
  margin-top: var(--sp-8);
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
`;

/* -------------------------------------------------------------- documentos */

export const Documentos = styled.section`
  padding-block: var(--section-y);
  background: var(--paper);
  border-top: 1px solid var(--border-hairline);
  scroll-margin-top: var(--nav-h);
`;

export const RelatorioDestaque = styled.div`
  display: grid;
  gap: var(--sp-5) var(--sp-6);
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  align-items: center;
  margin-bottom: var(--sp-7);
  scroll-margin-top: calc(var(--nav-h) + var(--sp-5));

  > div:first-child {
    display: flex;
    flex-direction: column;
    gap: var(--sp-5);
    align-items: flex-start;
    max-width: 46ch;

    h3 {
      font-size: var(--fs-h3);
      line-height: 1.15;
      letter-spacing: -0.01em;
      font-weight: 700;
      color: var(--brand-800);
      text-wrap: balance;
    }
  }

  > div:last-child {
    display: flex;
    justify-content: flex-end;
    perspective: 1400px;

    @media (max-width: 640px) {
      justify-content: center;
    }
  }
`;

/* Capa de documento. Com a capa do Squidex, mostra a imagem; sem ela, a capa
   verde com a logo -- o "provisório" do protótipo, agora como padrão. */
export const Capa = styled.span`
  position: relative;
  display: block;
  width: 100%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  /* "Cor da capa" do Squidex; vazio, o verde da Copasul. */
  background: ${({ $cor }) => $cor || 'var(--brand-800)'};

  ${({ $grande }) =>
    $grande
      ? css`
          max-width: 320px;
          border-radius: 4px 10px 10px 4px;
          box-shadow: 0 28px 60px rgba(27, 36, 32, 0.28), 0 6px 16px rgba(27, 36, 32, 0.12);
          transform: rotateY(-8deg);
          transform-origin: left center;
        `
      : css`
          border-radius: 2px 6px 6px 2px;
          box-shadow: 0 8px 20px rgba(27, 36, 32, 0.16);
          transition: transform var(--t-fast);
        `}

  > img.capa {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  /* Lombada. */
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${({ $grande }) => ($grande ? '14px' : '7px')};
    background: linear-gradient(90deg, rgba(0, 0, 0, 0.35) 0%, rgba(0, 0, 0, 0.05) 60%, rgba(255, 255, 255, 0.1) 100%);
  }

  > span {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    gap: var(--sp-2);
    padding: ${({ $grande }) =>
      $grande ? 'var(--sp-6) var(--sp-6) var(--sp-6) var(--sp-7)' : 'var(--sp-4) var(--sp-4) var(--sp-4) var(--sp-5)'};
    /* O verde padrão mantém o degradê do protótipo; cor escolhida ganha só uma
       sombra leve embaixo, para aparecer como foi escolhida. */
    background: ${({ $cor, $clara }) =>
      $cor
        ? `linear-gradient(0deg, rgba(0, 0, 0, ${$clara ? 0.06 : 0.22}) 0%, rgba(0, 0, 0, 0) 60%)`
        : 'linear-gradient(0deg, rgba(10, 58, 31, 0.96) 30%, rgba(10, 58, 31, 0.55) 100%)'};

    img {
      width: ${({ $grande }) => ($grande ? '130px' : '72px')};
      height: auto;
      display: block;
    }

    b {
      margin-top: auto;
      font-size: ${({ $grande }) => ($grande ? 'var(--fs-h3)' : 'var(--fs-small)')};
      line-height: 1.15;
      color: ${({ $clara }) => ($clara ? 'var(--ink-900)' : 'var(--white)')};
    }

    i {
      font-style: normal;
      font-size: ${({ $grande }) => ($grande ? 'var(--fs-h4)' : 'var(--fs-eyebrow)')};
      font-weight: 700;
      color: ${({ $cor, $clara }) => {
        if ($clara) return 'var(--brand-800)';
        return $cor ? 'rgba(255, 255, 255, 0.8)' : 'var(--brand-400)';
      }};
    }
  }
`;

export const Materiais = styled.div`
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-hairline);

  > div:first-child {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: var(--sp-4);
    flex-wrap: wrap;
    margin-bottom: var(--sp-4);
  }

  > div:last-child {
    display: flex;
    flex-wrap: wrap;
    gap: var(--sp-5);
  }
`;

export const Material = styled.a`
  flex: 1 1 140px;
  min-width: 0;
  max-width: 240px;
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  padding: 0;
  border: none;
  background: none;
  font-family: inherit;
  text-align: left;
  text-decoration: none;
  cursor: pointer;

  &:hover ${Capa} {
    transform: translateY(-3px);
  }

  > span:last-child {
    display: flex;
    flex-direction: column;
    gap: var(--sp-1);

    b {
      font-size: var(--fs-small);
      line-height: 1.35;
      color: var(--text-strong);
    }
    small {
      font-size: var(--fs-eyebrow);
      color: var(--text-muted);
    }
  }
`;

export const VerMais = styled.span`
  aspect-ratio: 3 / 4;
  border-radius: 2px 6px 6px 2px;
  border: 1px dashed var(--border-strong);
  background: var(--white);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-3);
  font-size: var(--fs-small);
  font-weight: 700;
  color: var(--accent);
  transition: border-color var(--t-fast);

  ${Material}:hover & {
    border-color: var(--accent);
  }

  > span {
    width: 44px;
    height: 44px;
    border-radius: var(--r-full);
    background: var(--accent-wash);
    display: grid;
    place-items: center;
  }
`;

export const Fundo = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-5);
  background: rgba(27, 36, 32, 0.62);
`;

export const Janela = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 720px;
  max-height: 82vh;
  overflow: hidden;
  background: var(--white);
  border-radius: var(--r-xl);
  box-shadow: var(--sh-lg);

  > div:first-child {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: var(--sp-4);
    padding: var(--sp-6) var(--sp-6) var(--sp-5);
    border-bottom: 1px solid var(--border-hairline);

    > div {
      display: flex;
      flex-direction: column;
      gap: var(--sp-2);
    }

    button {
      width: 44px;
      height: 44px;
      flex-shrink: 0;
      border-radius: var(--r-full);
      border: 1px solid var(--border-strong);
      background: var(--white);
      color: var(--brand-600);
      display: grid;
      place-items: center;
      cursor: pointer;

      &:hover {
        border-color: var(--accent);
      }
    }
  }

  > ul {
    overflow-y: auto;
    padding: var(--sp-5) var(--sp-6) var(--sp-6);
    display: flex;
    flex-direction: column;
    gap: var(--sp-3);
  }

  li a {
    display: grid;
    grid-template-columns: 44px 1fr auto;
    gap: var(--sp-4);
    align-items: center;
    padding: var(--sp-4);
    border: 1px solid var(--border-hairline);
    border-radius: var(--r-lg);
    text-decoration: none;
    transition: border-color var(--t-fast);

    &:hover {
      border-color: var(--accent);
    }

    > span {
      display: flex;
      flex-direction: column;
      gap: var(--sp-1);
      min-width: 0;
    }

    b {
      font-size: var(--fs-small);
      line-height: 1.35;
      color: var(--text-strong);
    }
    small {
      font-size: var(--fs-eyebrow);
      color: var(--text-muted);
    }
    i {
      width: 36px;
      height: 36px;
      border-radius: var(--r-full);
      background: var(--accent-wash);
      color: var(--accent);
      display: grid;
      place-items: center;
    }
  }

  @media (max-width: 600px) {
    > div:first-child,
    > ul {
      padding-inline: var(--sp-4);
    }
    li a {
      grid-template-columns: 32px 1fr auto;
    }
  }
`;
